import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { InputFieldset } from "ui/inputFieldset";
import { Label } from "../../ui/label";
import { Badge } from "../../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Switch } from "../../ui/switch";
import {
  DollarSign,
  Plus,
  Search,
  Edit2,
  Check,
  Users,
  Globe,
  Filter,
  X,
  ArrowUp,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import {
  AgeRuleApi,
  CountryApi,
  createVisaFee,
  getAgeRules,
  getCountries,
  getVisaFees,
  updateVisaFee,
  updateVisaFeeStatus,
} from "../../services/visaFeeMasterService";
import { useScreenPermission } from "../../hooks/useScreenPermission";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";
import Pagination from "ui/pagination";
import { MultiSelect } from "ui/visaMulti-select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "ui/tooltip";

// Types
interface VisaFee {
  id: string;
  nationality: string;
  currency: string;
  infantFee: number;
  childFee: number;
  adultFee: number;
  seniorFee: number;
  effectiveStartDate: string;
  effectiveEndDate: string;
  isActive: boolean;
}

type PaxType = "infant" | "child" | "senior" | "adult";

export const VisaFeeMasterPage = () => {
  const { setPageHeader } = usePageHeader();
  const [visaFees, setVisaFees] = useState<VisaFee[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingFee, setEditingFee] = useState<VisaFee | null>(null);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [countries, setCountries] = useState<CountryApi[]>([]);
  const [ageRules, setAgeRules] = useState<AgeRuleApi[]>([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const {
    canAdd,
    canEdit,
    screenName,
    icon: ScreenIcon,
  } = useScreenPermission();

  const [formData, setFormData] = useState({
    nationality: ["ALL_NATIONALITIES"] as string[],
    currency: "USD",
    infant: "" as number | string,
    child: "" as number | string,
    senior: "" as number | string,
    adult: "" as number | string,
    effectiveStartDate: new Date().toISOString().slice(0, 10),
    effectiveEndDate: new Date().toISOString().slice(0, 10),
    isActive: true,
  });

  const [filters, setFilters] = useState({
    nationality: "all",
    status: "all",
    effectiveStartDate: "",
    effectiveEndDate: "",
  });

  const [errors, setErrors] = useState<{
    infant?: string;
    child?: string;
    senior?: string;
    adult?: string;
    effectiveStartDate?: string;
    effectiveEndDate?: string;
  }>({});

  useEffect(() => {
    setPageHeader({
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    if (ageRules.length === 0) return;

    loadVisaFees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ageRules,
    currentPage,
    pageSize,
    filters,
    searchTerm,
  ]);

  useEffect(() => {
    loadAgeRules();
  }, []);

  const loadAgeRules = async () => {
    try {
      setLoading(true);
      const res = await getAgeRules();

      setAgeRules(res.filter((r) => r.isActive));
    } catch {
      toast.error("Failed to load age rules");
    } finally {
      setLoading(false);
    }
  };

  const loadVisaFees = async () => {
    try {
      setLoading(true);
      const res = await getVisaFees({
        nationality: searchTerm.trim()
          ? searchTerm.trim()
          : filters.nationality === "all"
            ? null
            : filters.nationality === "ALL_NATIONALITIES"
              ? "*"
              : filters.nationality,

        status:
          filters.status === "all"
            ? null
            : filters.status === "active",

        effectiveStartDate:
          filters.effectiveStartDate || null,

        effectiveEndDate:
          filters.effectiveEndDate || null,

        pageNo: currentPage,
        pageSize,
      });

      setVisaFees(
        res.items.map((item) => ({
          id: item.publicId,
          nationality:
            item.nationalities === "*"
              ? "ALL_NATIONALITIES"
              : item.nationalities
                .split(",")
                .map((x) => x.trim())
                .join(", "),
          currency: item.currency.toUpperCase(),
          infantFee: item.infantFee,
          childFee: item.childFee,
          adultFee: item.adultFee,
          seniorFee: item.seniorFee,
          effectiveStartDate: item.effectiveFrom,
          effectiveEndDate: item.effectiveTo,
          isActive: item.status,
        })),
      );
      setTotalCount(res.totalCount);
    } catch {
      toast.error("Failed to load visa fees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = async () => {
    try {
      setLoading(true);
      const res = await getCountries();
      setCountries(res.filter((c) => c.isActive));
    } catch {
      toast.error("Failed to load countries");
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  const applyFilters = (fees: VisaFee[]) => {
    return fees.filter((fee) => {
      if (filters.nationality !== "all") {
        if (filters.nationality === "ALL_NATIONALITIES") {
          if (fee.nationality !== "ALL_NATIONALITIES") {
            return false;
          }
        } else {
          if (fee.nationality !== filters.nationality) {
            return false;
          }
        }
      }

      if (filters.status === "active" && !fee.isActive) return false;
      if (filters.status === "inactive" && fee.isActive) return false;

      if (
        filters.effectiveStartDate &&
        fee.effectiveStartDate !== filters.effectiveStartDate
      ) {
        return false;
      }

      if (
        filters.effectiveEndDate &&
        fee.effectiveEndDate !== filters.effectiveEndDate
      ) {
        return false;
      }
      return true;
    });
  };

  const processedFees = applyFilters(visaFees);

  const hasActiveFilters =
    filters.nationality !== "all" ||
    filters.status !== "all" ||
    filters.effectiveStartDate !== "" ||
    filters.effectiveEndDate !== "";

  const handleOpenDialog = (fee?: VisaFee) => {
    if (fee) {
      setEditingFee(fee);
      setFormData({
        nationality:
          fee.nationality === "ALL_NATIONALITIES"
            ? ["ALL_NATIONALITIES"]
            : fee.nationality.split(",").map((x) => x.trim()),
        currency: fee.currency,
        infant: fee.infantFee,
        child: fee.childFee,
        senior: fee.seniorFee,
        adult: fee.adultFee,
        effectiveStartDate: fee.effectiveStartDate,
        effectiveEndDate: fee.effectiveEndDate,
        isActive: fee.isActive,
      });
    } else {
      setEditingFee(null);
      setFormData({
        nationality: ["ALL_NATIONALITIES"],
        currency: "USD",
        infant: "",
        child: "",
        senior: "",
        adult: "",
        effectiveStartDate: new Date().toISOString().slice(0, 10),
        effectiveEndDate: new Date().toISOString().slice(0, 10),
        isActive: true,
      });
    }

    setShowDialog(true);
    setErrors({});
  };

  // Handle save
  const handleSave = async () => {
    const newErrors: typeof errors = {};

    const paxTypes: PaxType[] = ["infant", "child", "senior", "adult"];
    paxTypes.forEach((type) => {
      if (formData[type] === "" || Number(formData[type]) < 0) {
        newErrors[type] = "Enter a valid amount";
      }
    });
    if (!formData.effectiveStartDate)
      newErrors.effectiveStartDate = "Effective date is required";
    if (!formData.effectiveEndDate)
      newErrors.effectiveEndDate = "Effective date is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);

      const payload = {
        nationalities: formData.nationality.includes("ALL_NATIONALITIES")
          ? ["*"]
          : formData.nationality,

        effectiveFrom: formData.effectiveStartDate,
        effectiveTo: formData.effectiveEndDate,

        infantFee: Number(formData.infant),
        childFee: Number(formData.child),
        adultFee: Number(formData.adult),
        seniorFee: Number(formData.senior),

        currency: formData.currency.toLowerCase(),
        status: formData.isActive,
      };

      if (editingFee) {
        await updateVisaFee({
          publicId: editingFee.id,
          ...payload,
          updatedBy: 1,
        });

        toast.success("Visa Fee updated successfully");
      } else {
        await createVisaFee({
          ...payload,
          createdBy: 1,
        });

        toast.success("Visa Fee created successfully");
      }

      setShowDialog(false);
      setErrors({});
      await loadVisaFees();
    } catch (err: unknown) {
      console.error(err);

      const apiData = axios.isAxiosError(err)
        ? (err.response?.data as {
            message?: string;
            errors?: string[];
          } | undefined)
        : undefined;

      const message =
        apiData?.message?.trim() ||
        apiData?.errors?.filter(Boolean).join(", ") ||
        (err instanceof Error ? err.message : "") ||
        "Failed to save visa fee";

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (fee: VisaFee) => {
    try {
      setLoading(true);

      const updatedStatus = !fee.isActive;

      await updateVisaFeeStatus(fee.id, updatedStatus);

      setVisaFees((prev) =>
        prev.map((f) =>
          f.id === fee.id
            ? {
              ...f,
              isActive: updatedStatus,
            }
            : f
        )
      );

      toast.success("Status updated successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };


  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-4 sm:space-y-6 pb-8 px-4 sm:px-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-4">
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />

              <Input
                placeholder="Search by nationality..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="h-10 pl-9 pr-9 shadow-sm"
              />

              {searchTerm && (
                <button
                  type="button"
                  onClick={async () => {
                    setSearchTerm("");
                    setCurrentPage(1);

                    await loadVisaFees();
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowStatistics(!showStatistics)}
              className="border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10 shrink-0"
            >
              {showStatistics ? (
                <ArrowUp className="w-5 h-5" />
              ) : (
                <BarChart3 className="w-5 h-5" />
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`shrink-0 ${showFilters
                ? "text-white"
                : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"
                }`}
              title="Advanced Filters"
            >
              <Filter className="w-4 h-4 sm:mr-0" />
            </Button>
            {canAdd && (
              <Button
                onClick={() => handleOpenDialog()}
                style={{ background: "#5B9BD5" }}
                className="text-white text-sm sm:text-base shrink-0"
              >
                <Plus className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Add Visa Fee</span>
                <span className="sm:hidden">Add Fee</span>
              </Button>
            )}
          </div>
        </div>

        {/* Statistics Cards */}
        {showStatistics && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" style={{ marginTop: "0px" }}>
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-blue-600">
                      Total Fees
                    </p>
                    <p className="text-xl sm:text-2xl font-bold text-blue-900">
                      {visaFees.length}
                    </p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-green-600">
                      Active Fees
                    </p>
                    <p className="text-xl sm:text-2xl font-bold text-green-900">
                      {visaFees.filter((f) => f.isActive).length}
                    </p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-amber-600">
                      Nationalities
                    </p>
                    <p className="text-xl sm:text-2xl font-bold text-amber-900">
                      {visaFees.filter((f) => f.nationality).length}
                    </p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Globe className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Advanced Filters */}
        {showFilters && (
          <Card className="bg-white border-[#5B9BD5]/30" style={{ gap: "0px", marginTop: "10px" }}>
            <CardHeader className="pb-3 px-3 sm:px-6 pt-3 sm:pt-6">
              <div className="flex items-center justify-between">
                <CardTitle
                  style={{ color: "#5B9BD5" }}
                  className="text-sm sm:text-base"
                >
                  Advanced Filters
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(false)}
                  className="text-gray-500 hover:text-gray-700 h-8 w-8 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 sm:space-y-4 px-3 sm:px-6 pb-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">

                {/* Nationality Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-nationality" className="text-sm">
                    Nationality
                  </Label>
                  <Select
                    value={filters.nationality}
                    onValueChange={(value) => {
                      setFilters({ ...filters, nationality: value });
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger
                      id="filter-nationality"
                      className="h-9 text-sm"
                    >
                      <SelectValue placeholder="All nationalities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Nationalities</SelectItem>
                      <SelectItem value="ALL_NATIONALITIES">
                        Generic (All)
                      </SelectItem>
                      {countries.map((country) => (
                        <SelectItem
                          key={country.countryId}
                          value={country.nationality ?? ""}
                        >
                          {country?.nationality}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Effective Start Date</Label>

                  <Input
                    type="date"
                    value={filters.effectiveStartDate}
                    onChange={(e) => {
                      setFilters({
                        ...filters,
                        effectiveStartDate: e.target.value,
                      });
                      setCurrentPage(1);
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Effective End Date</Label>

                  <Input
                    type="date"
                    value={filters.effectiveEndDate}
                    onChange={(e) => {
                      setFilters({
                        ...filters,
                        effectiveEndDate: e.target.value,
                      });
                      setCurrentPage(1);
                    }}
                  />
                </div>

                {/* Status Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-status" className="text-sm">
                    Status
                  </Label>
                  <Select
                    value={filters.status}
                    onValueChange={(value) => {
                      setFilters({ ...filters, status: value });
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger id="filter-status" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active Only</SelectItem>
                      <SelectItem value="inactive">Inactive Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>


              </div>

              {/* Active Filters Display */}
              {hasActiveFilters && (
                <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 pt-3 border-t">
                  <span className="text-xs sm:text-sm text-gray-600 font-medium">
                    Active Filters:
                  </span>
                  {filters.nationality !== "all" && (
                    <Badge>{filters.nationality}</Badge>
                  )}

                  {filters.status !== "all" && (
                    <Badge>{filters.status}</Badge>
                  )}

                  {filters.effectiveStartDate && (
                    <Badge>
                      Start: {filters.effectiveStartDate}
                    </Badge>
                  )}

                  {filters.effectiveEndDate && (
                    <Badge>
                      End: {filters.effectiveEndDate}
                    </Badge>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFilters({
                        nationality: "all",
                        status: "all",
                        effectiveStartDate: "",
                        effectiveEndDate: "",
                      });

                      setCurrentPage(1);
                    }}

                    className="text-red-600 border-red-300 hover:bg-red-50 text-xs sm:text-sm w-full sm:w-auto sm:ml-auto h-8"
                  >
                    <X className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    Clear All
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Table */}
        <Card
          className="bg-white border-[#5B9BD5]/30"
          style={{ gap: "0px", marginTop: "10px" }}
        >
          <CardContent style={{ padding: "0px 0px 10px 0px" }}>
            {/* Desktop Table View */}
            <div className="hidden lg:block w-full">
              <Table className="w-full table-fixed">
                <TableHeader>
                  <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                    {(["Infant", "Child", "Senior", "Adult"] as const).map(
                      (label) => (
                        <TableHead
                          key={label}
                          style={{ color: "#5B9BD5" }}
                          className="w-20 px-2 py-3 text-center text-xs"
                        >
                          {label}
                        </TableHead>
                      ),
                    )}
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs w-48"
                    >
                      Nationality
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs w-36"
                    >
                      Effective Start Date
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs w-36"
                    >
                      Effective End Date
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs w-28"
                    >
                      Status
                    </TableHead>
                    {canEdit && (
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs text-right w-20"
                      >
                        Actions
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {processedFees.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="text-center py-8 px-4 text-gray-500 text-sm"
                      >
                        No visa fees found
                      </TableCell>
                    </TableRow>
                  ) : (
                    processedFees.map((fee) => (
                      <TableRow key={fee.id} className="hover:bg-sky-50/30">
                        {(
                          ["infant", "child", "senior", "adult"] as PaxType[]
                        ).map((type) => (
                          <TableCell
                            key={type}
                            className="w-20 px-2 py-3 text-center"
                          >
                            <span className="inline-flex justify-center text-sm font-semibold text-slate-800">
                              {type === "infant"
                                ? fee.infantFee
                                : type === "child"
                                  ? fee.childFee
                                  : type === "senior"
                                    ? fee.seniorFee
                                    : (fee.adultFee.toLocaleString() ?? "—")}
                            </span>
                          </TableCell>
                        ))}
                        <TableCell className="px-4 py-3">
                          {(() => {
                            const nationalities =
                              fee.nationality === "ALL_NATIONALITIES"
                                ? ["All Nationalities"]
                                : fee.nationality
                                  .split(",")
                                  .map((n) => n.trim());

                            const showTooltip = nationalities.length > 2;

                            const content = (
                              <div className="flex flex-wrap gap-1 max-w-[220px] cursor-default">
                                {nationalities.slice(0, 2).map((name) => (
                                  <Badge
                                    key={name}
                                    className="bg-purple-100 text-purple-700 border-purple-300"
                                  >
                                    {name}
                                  </Badge>
                                ))}

                                {showTooltip && (
                                  <Badge className="bg-gray-100 text-gray-700">
                                    +{nationalities.length - 2}
                                  </Badge>
                                )}
                              </div>
                            );

                            if (!showTooltip) {
                              return content;
                            }

                            return (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    {content}
                                  </TooltipTrigger>

                                  <TooltipContent
                                    side="top"
                                    className="bg-slate-900 text-white rounded-lg px-5 py-4 shadow-xl w-[650px] max-w-[650px]"
                                  >
                                    <div className="flex flex-wrap gap-3">
                                      {nationalities.map((name) => (
                                        <Badge
                                          key={name}
                                          className="bg-purple-100 text-purple-700 border border-purple-300"
                                        >
                                          {name}
                                        </Badge>
                                      ))}
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-sm text-gray-700">
                            {formatDate(fee.effectiveStartDate)}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-sm text-gray-700">
                            {formatDate(fee.effectiveEndDate)}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={fee.isActive}
                              onCheckedChange={() => toggleActive(fee)}
                            />
                            <span className="text-xs text-gray-600">
                              {fee.isActive ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </TableCell>
                        {canEdit && (
                          <TableCell className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleOpenDialog(fee)}
                                className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                title="Edit fee"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden divide-y divide-gray-200">
              {processedFees.length === 0 ? (
                <div className="text-center py-8 px-4 text-gray-500 text-sm">
                  No visa fees found
                </div>
              ) : (
                processedFees.map((fee) => (
                  <div
                    key={fee.id}
                    className="p-4 hover:bg-sky-50/30 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 flex-1">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                          <Users className="w-5 h-5 text-[#5B9BD5]" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {fee.nationality === "ALL_NATIONALITIES"
                              ? "All Nationalities"
                              : fee.nationality}
                          </h3>
                          <p className="text-xs text-gray-600">
                            Effective {formatDate(fee.effectiveStartDate)}
                          </p>
                          <p className="text-xs text-gray-600">
                            Effective {formatDate(fee.effectiveEndDate)}
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={fee.isActive}
                        onCheckedChange={() => toggleActive(fee)}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {(
                        ["infant", "child", "senior", "adult"] as PaxType[]
                      ).map((type) => (
                        <div
                          key={type}
                          className="rounded-lg border border-sky-100 bg-sky-50 p-2"
                        >
                          <p className="text-[11px] capitalize text-gray-500">
                            {type}
                          </p>
                          <p className="text-sm font-semibold text-gray-900">
                            {type === "infant"
                              ? fee.infantFee
                              : type === "child"
                                ? fee.childFee
                                : type === "senior"
                                  ? fee.seniorFee
                                  : (fee.adultFee ?? "—")}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenDialog(fee)}
                        className="flex-1 text-[#5B9BD5] border-[#5B9BD5]/30 hover:bg-sky-50 h-8"
                      >
                        <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                        Edit
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            label="visa fees"
          />
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto w-[calc(100vw-2rem)] sm:w-full mx-4 sm:mx-auto">
            <DialogHeader>
              <DialogTitle
                style={{ color: "#5B9BD5" }}
                className="text-base sm:text-lg"
              >
                {editingFee ? "Edit Visa Fee" : "Add Visa Fee"}
              </DialogTitle>
              <DialogDescription className="text-xs sm:text-sm">
                {editingFee
                  ? "Update visa fee detailssss"
                  : "Create a new visa fee configurationnnnn"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 sm:space-y-4 py-3 sm:py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <InputFieldset
                    label="Effective Start Date"
                    required
                    id="effectiveStartDate"
                    type="date"
                    value={formData.effectiveStartDate}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        effectiveStartDate: e.target.value,
                      })
                    }
                    className="text-sm"
                  />
                  {errors.effectiveStartDate && (
                    <p className="text-xs text-red-600">
                      {errors.effectiveStartDate}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Effective End Date"
                    required
                    id="effectiveEndDate"
                    type="date"
                    value={formData.effectiveEndDate}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        effectiveEndDate: e.target.value,
                      })
                    }
                    className="text-sm"
                  />
                  {errors.effectiveEndDate && (
                    <p className="text-xs text-red-600">
                      {errors.effectiveEndDate}
                    </p>
                  )}
                </div>
              </div>

              <div
                className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4"
                style={{ marginTop: "5px" }}
              >
                <div className="space-y-2">
                  <fieldset className="w-full rounded-[0.6rem] border-0 bg-transparent px-3 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)] transition-all focus-within:shadow-[inset_0_0_0_1px_rgba(59,130,246,0.6),0_0_0_3px_rgba(59,130,246,0.25)]">
                    <legend className="bg-white">
                      <span className="text-sm font-medium text-gray-700">
                        Nationality
                      </span>
                      <span className="ml-1 text-red-500">*</span>
                    </legend>
                    <div className="relative">
                    <MultiSelect
                      closeOnOutsideClick
                      options={[
                        {
                          value: "ALL_NATIONALITIES",
                          label: "All Nationalities",
                        },
                        ...countries.flatMap((country) => {
                          const nationality = country.nationality?.trim();
                          return nationality
                            ? [{ value: nationality, label: nationality }]
                            : [];
                        }),
                      ]}
                      selected={formData.nationality}
                      onChange={(selected) =>
                        setFormData((prev) => {
                          const previouslySelectedAll =
                            prev.nationality.includes("ALL_NATIONALITIES");
                          const selectedAll =
                            selected.includes("ALL_NATIONALITIES");

                          const nationality = selectedAll
                            ? previouslySelectedAll
                              ? selected.filter(
                                (value) => value !== "ALL_NATIONALITIES",
                              )
                              : ["ALL_NATIONALITIES"]
                            : selected;

                          return { ...prev, nationality };
                        })
                      }
                      placeholder="Select nationalities..."
                    />
                    </div>
                  </fieldset>
                </div>

                <div className="self-start">
                  <InputFieldset
                    label="Currency"
                    id="currency"
                    required
                    value="USD"
                    readOnly
                    className="text-sm"
                  />
                </div>
              </div>

              <div className="rounded-xl border border-sky-100 bg-sky-50/50 p-3 sm:p-4">
                <p className="mb-3 text-sm font-semibold text-slate-800">
                  Pax-wise Visa Fee
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {(["infant", "child", "senior", "adult"] as PaxType[]).map(
                    (type) => (
                      <div key={type} className="space-y-1.5">
                        <InputFieldset
                          label={`${type.charAt(0).toUpperCase()}${type.slice(1)} Fee`}
                          required
                          id={`${type}Fee`}
                          type="number"
                          min="0"
                          placeholder="0"
                          value={formData[type]}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData({
                              ...formData,
                              [type]: value === "" ? "" : Number(value),
                            });
                            setErrors((prev) => ({
                              ...prev,
                              [type]: undefined,
                            }));
                          }}
                          className="text-sm"
                        />
                        {errors[type] && (
                          <p className="text-xs text-red-600">{errors[type]}</p>
                        )}
                      </div>
                    ),
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                <Label htmlFor="isActive" className="text-sm cursor-pointer">
                  Active Status
                </Label>
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isActive: checked })
                  }
                />
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-4">
              <Button
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="text-sm w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                style={{ background: "#5B9BD5" }}
                className="text-white text-sm w-full sm:w-auto"
              >
                {editingFee ? "Update" : "Add"} Visa Fee
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
